import {
  Box,
  Grid,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Modal,
  Fade,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { StakingPoolCard } from "./StakingPoolCard";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../store";
import { fetchStakingData } from "../../store/user/loader";
import {
  ClaimStakePool,
  StakePool,
  UnstakePool,
} from "../../store/user/actions";
import { InstructionsModal } from "./InstructionsModal";
import "../Tools.css"; // Import wood-themed styling

// Icons
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import StarsIcon from "@mui/icons-material/Stars";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SortIcon from "@mui/icons-material/Sort";
import PercentIcon from "@mui/icons-material/Percent";
import TimerIcon from "@mui/icons-material/Timer";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface Pool {
  id: number;
  min_stake: string;
  total_staked: string;
  stake_delay: number;
  base_apr: any;
  monthly_increase: number;
  is_open: boolean;
  reward_type: string;
}

interface Staker {
  id: number;
  pool_id: number;
  username: string;
  staked_amount: string;
  pending_rewards: string;
  end_time: number;
  start_time: number;
  last_reward_update: number;
}
export const Staking = () => {
  const dispatch = useDispatch<any>();
  const userModule = useSelector((state: AppState) => state.userModule);
  const { loginInfo, gameStatus } = userModule;

  const [pools, setPools] = useState<Pool[]>([]);
  const [stakerInfo, setStakerInfo] = useState<Staker | null>(null);
  const [selectedPool, setSelectedPool] = useState<Pool | null>(null);
  const [stakeAmount, setStakeAmount] = useState("");
  const [isStaking, setIsStaking] = useState(false);
  const [showStakeDialog, setShowStakeDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [stakers, setAllStakers] = useState<Staker[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (loginInfo?.account) {
      refreshStakingData();
    }
  }, [loginInfo?.account]);

  const handlePoolSelection = (event: any) => {
    setSelectedPoolId(event.target.value as number);
  };

  const filteredStakers = selectedPoolId
    ? stakers
        .filter((staker) => staker.pool_id === selectedPoolId)
        ?.sort(
          (a, b) =>
            parseFloat(b.staked_amount?.split(" ")?.[0]) -
            parseFloat(a.staked_amount?.split(" ")?.[0])
        )
    : stakers;

  const refreshStakingData = async () => {
    if (loginInfo?.account) {
      setIsLoading(true);
      try {
        const data = await fetchStakingData();
        setPools(data.pool_info);
        setAllStakers(data.staking_pool);
        var foundInStakingPool = data.staking_pool?.find(
          (p: any) => p.username === loginInfo.account
        );
        setStakerInfo(foundInStakingPool || null);
      } catch (error) {
        console.error("Error fetching staking data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleStake = async (pool: Pool) => {
    setSelectedPool(pool);
    setShowStakeDialog(true);
  };

  const handleConfirmStake = async () => {
    if (!selectedPool || !loginInfo?.account) return;

    setIsStaking(true);
    const selectedID = selectedPool.id || 0;
    try {
      const action = {
        account: "waxdeer",
        name: "transfer",
        authorization: [
          {
            actor: loginInfo.account,
            permission: "active",
          },
        ],
        data: {
          from: loginInfo.account,
          to: "waxdeer",
          quantity: stakeAmount,
          memo: `stake:${selectedPool.id}`,
        },
      };

      await dispatch(StakePool(selectedID, parseFloat(stakeAmount)));

      setShowStakeDialog(false);
    } catch (error) {
      console.error("Staking failed:", error);
    } finally {
      setIsStaking(false);
    }
  };

  const handleClaim = async (stakeId: number) => {
    if (!loginInfo?.account) return;

    try {
      const action = {
        account: "waxdeer",
        name: "claim",
        authorization: [
          {
            actor: loginInfo.account,
            permission: "active",
          },
        ],
        data: {
          stake_id: stakeId,
        },
      };

      await dispatch(ClaimStakePool(stakeId));

      await refreshStakingData();
    } catch (error) {
      console.error("Claim failed:", error);
    }
  };
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  const handleUnstake = async (stakeId: number) => {
    if (!loginInfo?.account) return;

    try {
      const action = {
        account: "waxdeer",
        name: "unstake",
        authorization: [
          {
            actor: loginInfo.account,
            permission: "active",
          },
        ],
        data: {
          stake_id: stakeId,
        },
      };

      await dispatch(UnstakePool(stakeId));

      await refreshStakingData();
    } catch (error) {
      console.error("Unstake failed:", error);
    }
  };

  return (
    <Box 
      sx={{ 
        position: "relative",
        minHeight: "calc(100vh - 76px)",
        background: "linear-gradient(135deg, #5c3c10 0%, #3d2c0a 100%)",
        padding: "24px",
        overflow: "hidden"
      }}
    >
      {/* Wood texture overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(45deg, rgba(92, 60, 16, 0.2) 25%, transparent 25%, transparent 50%, rgba(92, 60, 16, 0.2) 50%, rgba(92, 60, 16, 0.2) 75%, transparent 75%, transparent)",
          backgroundSize: "10px 10px",
          opacity: 0.3,
          zIndex: 0,
        }}
      />

      {/* Header */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 4, 
          background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
          backgroundSize: '100% 100%',
          borderRadius: '16px',
          position: 'relative', 
          overflow: 'hidden',
          border: '4px solid #7d5014',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
        }}
      >
        <Box sx={{ 
          position: 'relative', 
          zIndex: 2, 
          display: 'flex', 
          flexDirection: {xs: 'column', sm: 'row'}, 
          alignItems: {xs: 'center', sm: 'flex-start'}, 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: {xs: 2, sm: 0} }}>
            <AccountBalanceIcon sx={{ color: "#f5e1a3", mr: 2, fontSize: "2.5rem" }} />
            <Box>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  color: "#f5e1a3", 
                  fontWeight: "bold",
                  textShadow: '2px 2px 4px rgba(0,0,0,0.6)'
                }}
              >
                ANTLERS Staking Pools
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.9)" sx={{ mt: 0.5, textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                Stake your ANTLERS tokens and earn rewards
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setShowInstructions(true)}
            sx={{
              color: "#f5e1a3",
              borderColor: "#f5e1a3",
              "&:hover": {
                borderColor: "#fff",
                backgroundColor: "rgba(245, 225, 163, 0.1)",
              },
              alignSelf: {xs: 'center', sm: 'flex-start'}
            }}
          >
            How to Stake?
          </Button>
        </Box>
        
        {/* Wood grain overlay */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
          opacity: 0.4, 
          zIndex: 1 
        }} />
        
        {/* Decorative elements */}
        <Box sx={{ 
          position: 'absolute', 
          right: {xs: -30, md: 30}, 
          top: 0, 
          opacity: 0.15, 
          zIndex: 1, 
          transform: 'rotate(-10deg)' 
        }}>
          <AccountBalanceIcon sx={{ fontSize: 120 }} />
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4, position: "relative", zIndex: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="stats-card" sx={{ 
            p: 2, 
            height: "auto",
            minHeight: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
              Total Pools
            </Typography>
            <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
              {pools.length}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="stats-card" sx={{ 
            p: 2, 
            height: "auto",
            minHeight: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
              Your Staked Amount
            </Typography>
            <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
              {stakerInfo ? stakerInfo.staked_amount : "0 ANTLERS"}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="stats-card" sx={{ 
            p: 2, 
            height: "auto",
            minHeight: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
              Your Pending Rewards
            </Typography>
            <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
              {stakerInfo ? stakerInfo.pending_rewards : "0 ANTLERS"}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="stats-card" sx={{ 
            p: 2, 
            height: "auto",
            minHeight: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
              Total Stakers
            </Typography>
            <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
              {stakers.length}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box className="wood-panel" sx={{ 
        borderRadius: "16px", 
        p: 3, 
        mb: 3, 
        position: "relative", 
        zIndex: 1 
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            mb: 3,
            width: "100%",
            '& .MuiTabs-indicator': {
              backgroundColor: '#f5e1a3',
            },
            '& .MuiTab-root': {
              color: 'rgba(245, 225, 163, 0.7)',
              '&.Mui-selected': {
                color: '#f5e1a3',
                fontWeight: 'bold',
              }
            }
          }}
          centered
        >
          <Tab 
            label="Staking Pools" 
            icon={<StarsIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Leaderboards" 
            icon={<LeaderboardIcon />}
            iconPosition="start"
          />
        </Tabs>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <CircularProgress sx={{ color: "#f5e1a3" }} />
          </Box>
        ) : (
          <>
            {activeTab === 0 && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                <Box 
                  sx={{ 
                    overflowY: "auto", 
                    mt: 0, 
                    height: "100%",
                    maxHeight: "60vh",
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(245, 225, 163, 0.3) rgba(92, 60, 16, 0.1)",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "rgba(92, 60, 16, 0.1)",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(245, 225, 163, 0.3)",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "rgba(245, 225, 163, 0.5)",
                    },
                  }}
                >
                  <Grid
                    container
                    spacing={3}
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    {pools?.map((pool: Pool | any) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={pool.id}
                      >
                        <StakingPoolCard
                          pool={pool}
                          stakerInfo={stakerInfo}
                          onStake={() => handleStake(pool)}
                          onClaim={() => stakerInfo && handleClaim(stakerInfo.id)}
                          onUnstake={() =>
                            stakerInfo && handleUnstake(stakerInfo.id)
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            )}
            
            {activeTab === 1 && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <FilterAltIcon sx={{ color: "#f5e1a3", mr: 1 }} />
                  <FormControl 
                    fullWidth
                    sx={{ 
                      '.MuiInputLabel-root': {
                        color: '#f5e1a3',
                      },
                      '.MuiOutlinedInput-root': {
                        color: 'white',
                        '.MuiOutlinedInput-notchedOutline': {
                          borderColor: '#7d5014',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f5e1a3',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f5e1a3',
                        }
                      },
                      '.MuiSelect-icon': {
                        color: '#f5e1a3',
                      }
                    }}
                  >
                    <InputLabel>Select Pool</InputLabel>
                    <Select
                      value={selectedPoolId || ""}
                      label="Select Pool"
                      onChange={(e) => handlePoolSelection(e)}
                    >
                      <MenuItem value="">All Pools</MenuItem>
                      {pools.map((pool) => (
                        <MenuItem key={pool.id} value={pool.id}>
                          Pool {pool.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Box className="stats-card" sx={{ 
                      p: 2, 
                      height: "auto", 
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}>
                      <Typography variant="body1" sx={{ color: "#f5e1a3", mb: 1 }}>
                        Total Stakers
                      </Typography>
                      <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
                        {filteredStakers?.length || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="stats-card" sx={{ 
                      p: 2, 
                      height: "auto", 
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}>
                      <Typography variant="body1" sx={{ color: "#f5e1a3", mb: 1 }}>
                        Total ANTLERS Staked
                      </Typography>
                      <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
                        {filteredStakers
                          ?.reduce(
                            (acc, staker) => acc + parseFloat(staker.staked_amount),
                            0
                          )
                          .toFixed(4) || "0"} ANTLERS
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: "50vh",
                    overflow: "auto",
                    backgroundColor: "transparent",
                    borderRadius: "12px",
                    border: "none",
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(245, 225, 163, 0.3) rgba(92, 60, 16, 0.1)",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "rgba(92, 60, 16, 0.1)",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(245, 225, 163, 0.3)",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "rgba(245, 225, 163, 0.5)",
                    },
                  }}
                >
                  <Table>
                    <TableHead
                      sx={{
                        backgroundColor: "rgba(92, 60, 16, 0.8)",
                        position: "sticky",
                        top: 0,
                        zIndex: 10
                      }}
                    >
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            color: "#f5e1a3", 
                            fontWeight: "bold",
                            borderBottom: "2px solid #7d5014",
                            padding: { xs: '8px 6px', md: '16px' },
                            whiteSpace: "nowrap"
                          }}
                        >
                          Rank
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            color: "#f5e1a3", 
                            fontWeight: "bold",
                            borderBottom: "2px solid #7d5014",
                            padding: { xs: '8px 6px', md: '16px' }
                          }}
                        >
                          Username
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            color: "#f5e1a3", 
                            fontWeight: "bold",
                            borderBottom: "2px solid #7d5014",
                            padding: { xs: '8px 6px', md: '16px' },
                            display: { xs: 'none', sm: 'table-cell' }
                          }}
                        >
                          Staked Amount
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            color: "#f5e1a3", 
                            fontWeight: "bold",
                            borderBottom: "2px solid #7d5014",
                            padding: { xs: '8px 6px', md: '16px' },
                            display: { xs: 'none', md: 'table-cell' }
                          }}
                        >
                          Lock Duration
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            color: "#f5e1a3", 
                            fontWeight: "bold",
                            borderBottom: "2px solid #7d5014",
                            padding: { xs: '8px 6px', md: '16px' }
                          }}
                        >
                          Daily Rewards
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody
                      sx={{
                        "& .MuiTableRow-root:nth-of-type(odd)": {
                          backgroundColor: "rgba(92, 60, 16, 0.2)",
                        },
                        "& .MuiTableRow-root:nth-of-type(even)": {
                          backgroundColor: "rgba(92, 60, 16, 0.4)",
                        },
                        "& .MuiTableRow-root:hover": {
                          backgroundColor: "rgba(245, 225, 163, 0.1)",
                        },
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid rgba(245, 225, 163, 0.1)",
                          color: "white",
                          padding: { xs: '8px 6px', md: '16px' }
                        }
                      }}
                    >
                      {filteredStakers
                        ?.sort(
                          (a, b) =>
                            parseFloat(b.staked_amount) -
                            parseFloat(a.staked_amount)
                        )
                        ?.map((staker, index) => {
                          const pool = pools.find(
                            (pool) => pool.id === staker.pool_id
                          );
                          const apr = parseFloat(pool?.base_apr || "0"); // Extract APR
                          const dailyReward =
                            (apr / 365) * parseFloat(staker.staked_amount); // Calculate daily reward
                          const stake_delay = pool?.stake_delay || 0;
                          const isCurrentUser = staker.username === loginInfo?.account;
                          
                          return (
                            <TableRow 
                              key={staker.id}
                              sx={{
                                ...(isCurrentUser && {
                                  backgroundColor: "rgba(245, 225, 163, 0.15) !important",
                                  fontWeight: "bold",
                                })
                              }}
                            >
                              <TableCell width="10%">
                                {index < 3 ? (
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {index === 0 && (
                                      <span style={{ color: "gold", marginRight: "5px" }}>🥇</span>
                                    )}
                                    {index === 1 && (
                                      <span style={{ color: "silver", marginRight: "5px" }}>🥈</span>
                                    )}
                                    {index === 2 && (
                                      <span style={{ color: "#cd7f32", marginRight: "5px" }}>🥉</span>
                                    )}
                                    {index + 1}
                                  </Box>
                                ) : (
                                  index + 1
                                )}
                              </TableCell>
                              <TableCell>
                                {isCurrentUser ? (
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography 
                                      noWrap 
                                      sx={{ 
                                        color: "#f5e1a3", 
                                        fontWeight: "bold",
                                        maxWidth: { xs: '80px', sm: '120px', md: 'unset' },
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                      }}
                                    >
                                      {staker.username} <span style={{ fontSize: "0.8em" }}>(You)</span>
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography 
                                    noWrap 
                                    sx={{ 
                                      maxWidth: { xs: '80px', sm: '120px', md: 'unset' },
                                      overflow: "hidden",
                                      textOverflow: "ellipsis"
                                    }}
                                  >
                                    {staker.username}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                {staker.staked_amount}
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <AccessTimeIcon sx={{ fontSize: "1rem", mr: 1, color: "#f5e1a3" }} />
                                  <Typography variant="body2" sx={{ color: "white" }}>
                                    {pool && pool.stake_delay ? `${pool.stake_delay / 86400} days` : "Loading..."}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {dailyReward?.toFixed(4) + " ANTLERS"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Stake Dialog */}
      <Dialog 
        open={showStakeDialog} 
        onClose={() => setShowStakeDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#3d2c0a",
            backgroundImage: "linear-gradient(135deg, #3d2c0a 0%, #5c3c10 100%)",
            border: "4px solid #7d5014",
            borderRadius: "16px",
            maxWidth: "95%",
            width: "450px",
            position: "relative"
          }
        }}
      >
        {/* Wood texture overlay */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
          opacity: 0.4, 
          zIndex: 0,
          borderRadius: "12px"
        }} />
        
        <DialogTitle sx={{ 
          color: "#f5e1a3", 
          fontWeight: "bold", 
          borderBottom: "1px solid rgba(245, 225, 163, 0.3)",
          position: "relative",
          zIndex: 1,
          pb: 2
        }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <StarsIcon sx={{ mr: 1, color: "#f5e1a3" }} />
            Stake ANTLERS Tokens
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, position: "relative", zIndex: 1 }}>
          {selectedPool && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
                Pool Information:
              </Typography>
              <Paper 
                elevation={0} 
                sx={{ 
                  backgroundColor: "rgba(0,0,0,0.2)", 
                  p: 2, 
                  borderRadius: "8px",
                  border: "1px solid rgba(245, 225, 163, 0.2)"
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <PercentIcon sx={{ color: "gold", mr: 1, fontSize: "1rem" }} />
                      <Typography variant="body2" sx={{ color: "white" }}>
                        APR: {(parseFloat(selectedPool.base_apr) * 100).toFixed(2)}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <TimerIcon sx={{ color: "lightblue", mr: 1, fontSize: "1rem" }} />
                      <Typography variant="body2" sx={{ color: "white" }}>
                        Lock: {selectedPool.stake_delay / 86400} days
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <InfoIcon sx={{ color: "#f5e1a3", mr: 1, fontSize: "1rem" }} />
                      <Typography variant="body2" sx={{ color: "#f5e1a3" }}>
                        Minimum stake: {selectedPool.min_stake}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Amount to Stake"
            type="number"
            fullWidth
            value={stakeAmount}
            onChange={(e) => setStakeAmount(e.target.value)}
            disabled={isStaking}
            sx={{
              '& .MuiInputLabel-root': {
                color: '#f5e1a3',
              },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: '#7d5014',
                },
                '&:hover fieldset': {
                  borderColor: '#f5e1a3',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#f5e1a3',
                }
              }
            }}
          />
          <Typography variant="caption" sx={{ color: "#f5e1a3", mt: 1, display: "block" }}>
            Note: Staking will lock your tokens for {(selectedPool?.stake_delay || 0) / 86400 || 0} days. You will earn rewards based on the APR.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, position: "relative", zIndex: 1, borderTop: "1px solid rgba(245, 225, 163, 0.2)" }}>
          <Button
            onClick={() => setShowStakeDialog(false)}
            disabled={isStaking}
            sx={{ 
              color: "#f5e1a3",
              borderColor: "#f5e1a3",
              '&:hover': {
                borderColor: "white",
                backgroundColor: "rgba(245, 225, 163, 0.1)"
              }
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmStake} 
            disabled={isStaking || !stakeAmount || parseFloat(stakeAmount) <= 0}
            variant="contained"
            sx={{
              backgroundColor: "#5c3c10",
              color: "#f5e1a3",
              '&:hover': {
                backgroundColor: "#4a300b",
              }
            }}
          >
            {isStaking ? <CircularProgress size={24} sx={{ color: "#f5e1a3" }} /> : "Stake"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instructions Modal */}
      <InstructionsModal
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
      />
    </Box>
  );
};
